import { DialogActions, DialogContent, DialogContentText, DialogTitle, Dialog as MuiDialog } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth } from 'contexts';
import { SelectedLocationContext } from 'contexts/selected-location';
import dayjs from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { useBillableEventsApi } from 'hooks/use-billable-event-api';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { searchByUnitNumber } from 'services';
import { getContractForSizeType } from 'services/contracts';
import { ActiveUnit, BillableEvent, BillableEventType, Contract, Role, defaultBillableEvent } from 'types';

dayjs.extend(utc);

export type BillableEventDialogInputs = {
  open: boolean;
  close: () => void;
  unit: ActiveUnit;
  existingBillableEvent: BillableEvent | null;
  onSuccess?: () => void;
};

const typeToPrettyString = (type: BillableEventType) => {
  switch (type) {
    case BillableEventType.LIFT_OFF:
      return 'Lift Off';
    case BillableEventType.LIFT_ON:
      return 'Lift On';
    case BillableEventType.DIG:
      return 'Dig';
    case BillableEventType.CREDIT:
      return 'Credit';
    case BillableEventType.OTHER:
    default:
      return 'Other';
  }
};

const typesRequiringAssocUnit = [BillableEventType.LIFT_ON];

/** Only these roles choose a type other than a lift, or a cost; the API prices every other role's lift. */
const TYPE_AND_COST_ROLES = [Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING];

const BillableEventDialog: FC<BillableEventDialogInputs> = ({
  unit,
  existingBillableEvent,
  open,
  close,
  onSuccess
}) => {
  // STATE
  const [billableEvent, setBillableEvent] = useState<BillableEvent>(
    existingBillableEvent ? { ...existingBillableEvent } : { ...defaultBillableEvent(), unit }
  );
  const [contract, setContract] = useState<Contract | null>(null);
  const [formValid, setFormValid] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assocUnitRequired, setAssocUnitRequired] = useState(false);
  const [assocUnitValid, setAssocUnitValid] = useState(false);
  const [assocUnitNumber, setAssocUnitNumber] = useState(existingBillableEvent?.associatedUnit?.unitNumber ?? '');
  const [assocUnitSearching, setAssocUnitSearching] = useState(false);

  const { userProfile } = useAuth();
  const { selectedLocation } = useContext(SelectedLocationContext);
  const formRef = useRef<HTMLFormElement>(null);

  // methods for Billable Events with feedback
  const { putBillableEvent, postBillableEvent, deleteBillableEvent } = useBillableEventsApi();

  // CALLBACKS
  const editBillableEvent = useCallback((partial: Partial<BillableEvent>) => {
    setBillableEvent(p => ({ ...p, ...partial }));
  }, []);

  const handleTimestampChange = (val: dayjs.Dayjs | null) => {
    editBillableEvent({ timestamp: val?.toISOString() ?? dayjs.utc().startOf('day').toISOString() });
  };

  const updateDefaultCost = useCallback(() => {
    if (!contract || !!existingBillableEvent) {
      return;
    }

    let cost = 0;
    switch (billableEvent.type) {
      case BillableEventType.LIFT_OFF:
        cost = unit.loaded ? contract.liftOffLoaded : contract.liftOffEmpty;
        break;
      case BillableEventType.LIFT_ON:
        cost = unit.loaded ? contract.liftOnLoaded : contract.liftOnEmpty;
        break;
      case BillableEventType.DIG:
        cost = unit.loaded ? contract.digLoaded : contract.digEmpty;
        break;
      case BillableEventType.CREDIT:
      case BillableEventType.OTHER:
      default:
        cost = 0;
    }
    editBillableEvent({ cost });
  }, [billableEvent.type, contract, editBillableEvent, existingBillableEvent, unit.loaded]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    // If existingBillableEvent is null, this is a new event
    if (existingBillableEvent === null) {
      postBillableEvent(assocUnitRequired ? billableEvent : { ...billableEvent, associatedUnit: undefined }).then(
        createdBillableEvent => {
          if (createdBillableEvent) {
            if (onSuccess) {
              onSuccess();
            }
            close();
          } else {
            // TODO show errors
          }
        }
      );
    } else {
      // Editing existing event
      putBillableEvent(
        assocUnitRequired ? billableEvent : { ...billableEvent, associatedUnit: undefined },
        existingBillableEvent.id
      ).then(updatedBillableEvent => {
        if (updatedBillableEvent) {
          if (onSuccess) {
            onSuccess();
          }
          close();
        } else {
          // TODO show errors
        }
      });
    }
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    close();
  };

  const doDeleteBillableEvent = useCallback(() => {
    setShowDeleteConfirm(false);
    if (!existingBillableEvent) {
      return;
    }
    deleteBillableEvent(existingBillableEvent.id).then(() => {
      if (onSuccess) {
        onSuccess();
      }
      close();
    });
  }, [close, existingBillableEvent, onSuccess, deleteBillableEvent]);

  // EFFECTS
  // Load contract — only while the dialog is open, since it's only used to suggest a default cost.
  useEffect(() => {
    if (!open || !unit.sizeType || !unit.customer || !selectedLocation) {
      return;
    }
    getContractForSizeType({
      sizeType: unit.sizeType ?? '',
      date: new Date(billableEvent.timestamp),
      customerId: unit.customer.id,
      locationId: selectedLocation.id
    }).then(c => setContract(c));
  }, [open, billableEvent.timestamp, selectedLocation, unit.customer, unit.sizeType]);

  // Update default cost when contract state changes
  useEffect(() => {
    updateDefaultCost();
  }, [updateDefaultCost]);

  // Update validity on model change
  useEffect(() => {
    setFormValid((formRef.current?.checkValidity() ?? true) && (assocUnitRequired ? assocUnitValid : true));
  }, [assocUnitRequired, assocUnitValid, billableEvent]);

  // Reset on input change
  useEffect(() => {
    setBillableEvent(existingBillableEvent ? { ...existingBillableEvent } : { ...defaultBillableEvent(), unit });
    setAssocUnitNumber(existingBillableEvent?.associatedUnit?.unitNumber ?? '');
    // If a contract is given and not editing an existing event, default to lift cost
    if (existingBillableEvent) {
      return;
    }
    editBillableEvent({ cost: contract?.liftOffEmpty ?? 0 });
  }, [contract?.liftOffEmpty, editBillableEvent, existingBillableEvent, unit]);

  // When event type changes, update suggested cost
  useEffect(() => updateDefaultCost(), [updateDefaultCost]);

  // Is associated unit required?
  useEffect(() => {
    const required = typesRequiringAssocUnit.includes(billableEvent.type);
    setAssocUnitRequired(required);
  }, [billableEvent.type]);

  // Debounced search for associated unit
  useEffect(() => {
    if (assocUnitNumber.length) {
      setAssocUnitSearching(true);
      const search = setTimeout(() => {
        searchByUnitNumber(assocUnitNumber, new Date(billableEvent.timestamp))
          .then(u => {
            editBillableEvent({ associatedUnit: u ?? undefined });
            setAssocUnitValid(!!u);
          })
          .finally(() => setAssocUnitSearching(false));
      }, 300);
      return () => clearTimeout(search);
    }
  }, [assocUnitNumber, billableEvent.timestamp, editBillableEvent]);

  return (
    // Need to use MUI Dialog here for now because this contains a date picker, and their
    // date picker doesn't work over a native HTML5 dialog because they manually add it
    // at the DOM root, putting it behind the top-layer dialog.
    <MuiDialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          background: 'white'
        }
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      }}
    >
      <DialogTitle>{!!existingBillableEvent ? 'Edit' : 'Add'} Billable Event</DialogTitle>
      <DialogContent>
        <form className="dialog-body" onSubmit={handleSubmit} ref={formRef} data-cy="add-billable-event-form">
          <div className="grid-big-2">
            <div>
              <label>Unit</label>
              <p className="text-big mt-2">{unit?.unitNumber}</p>
            </div>
            <div>
              <label className="required">Cost</label>
              <input
                type="number"
                step={'any'}
                min={0}
                className="mt-2 w-100"
                value={billableEvent.cost ?? 0}
                onChange={e => editBillableEvent({ cost: Number(e.target.value) })}
                disabled={!TYPE_AND_COST_ROLES.includes(userProfile.role)}
                data-cy="billable-event-cost-input"
              />
            </div>
            <div>
              <label>Type</label>
              <select
                className="w-100 mt-2 bg-white"
                value={billableEvent.type}
                onChange={e =>
                  editBillableEvent({
                    type: e.target.value as BillableEventType
                  })
                }
                data-cy="billable-event-type-select"
              >
                {Object.values(BillableEventType)
                  .filter(
                    bt =>
                      TYPE_AND_COST_ROLES.includes(userProfile.role) ||
                      [BillableEventType.LIFT_ON, BillableEventType.LIFT_OFF].includes(bt)
                  )
                  .map(value => {
                    return (
                      <option value={value} key={value}>
                        {typeToPrettyString(value)}
                      </option>
                    );
                  })}
              </select>
            </div>
            <div>
              <label className={billableEvent.type === BillableEventType.OTHER ? 'required' : undefined}>Note</label>
              <input
                type="text"
                required={billableEvent.type === BillableEventType.OTHER}
                className="w-100 mt-2"
                value={billableEvent.label}
                onChange={e => editBillableEvent({ label: e.target.value })}
                data-cy="billable-event-note-input"
              />
            </div>

            <div>
              <label>Operator</label>
              <input
                type="text"
                className="mt-2 w-100"
                value={billableEvent.operator}
                onChange={e => editBillableEvent({ operator: e.target.value })}
                data-cy="billable-event-operator-input"
              />
            </div>
            <div>
              <label>Date</label>
              <div className="mt-2">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    sx={{ backgroundColor: 'var(--white)' }}
                    slotProps={{
                      textField: {
                        margin: 'none',
                        required: true,
                        size: 'small',
                        className: 'w-100'
                      }
                    }}
                    value={dayjs(billableEvent.timestamp ?? new Date()).utc()}
                    onChange={handleTimestampChange}
                    maxDate={dayjs.utc()}
                  />
                </LocalizationProvider>
              </div>
            </div>
            {assocUnitRequired && (
              <>
                <div>
                  <label className="required">Associated Unit</label>
                  <input
                    type="text"
                    className="mt-2 w-100"
                    value={assocUnitNumber}
                    onChange={e => setAssocUnitNumber(e.target.value.toUpperCase())}
                    data-cy="billable-event-associated-unit-input"
                  />
                  {(!assocUnitNumber.length || assocUnitSearching) && <p className="mt-2">&nbsp;</p>}
                  {!!assocUnitNumber.length && !assocUnitSearching && (
                    <p className={`${assocUnitValid ? 'text-green' : 'text-danger'} mt-2`}>
                      {assocUnitValid ? 'Unit Found' : 'Unit Not Found'}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="d-flex justify-content-between align-items-center mt-4">
            <div>
              {!!existingBillableEvent && (
                <>
                  <button type="button" className="btn-danger" onClick={() => setShowDeleteConfirm(true)}>
                    Delete
                  </button>
                  <MuiDialog open={showDeleteConfirm}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                      <DialogContentText>Are you sure you want to delete this billable event?</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <button type="button" className="btn-outline" onClick={() => setShowDeleteConfirm(false)}>
                        Cancel
                      </button>
                      <button type="button" className="btn-danger" onClick={() => doDeleteBillableEvent()}>
                        Delete
                      </button>
                    </DialogActions>
                  </MuiDialog>
                </>
              )}
            </div>
            <div className="d-flex g-3">
              <button type="button" className="btn-outline" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={!formValid}>
                Submit
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </MuiDialog>
  );
};

export default BillableEventDialog;
